import { useEffect, type FC } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withDelay, withTiming } from 'react-native-reanimated';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';

const POP_IN_OVERSHOOT = 2;
const POP_IN_TIMING = { duration: Dimens.animationDuration300, easing: Easing.out(Easing.back(POP_IN_OVERSHOOT)) };
const NUMBER_FADE_TIMING = { duration: Dimens.animationDuration250, easing: Easing.out(Easing.cubic) };
const NUMBER_FADE_DELAY = Dimens.animationDuration100;

interface Props {
	count: number;
	animatesIn?: boolean;
}

const NotificationBadge: FC<Props> = ({ count, animatesIn = false }) => {
	const scale = useSharedValue(animatesIn ? Dimens.scale0 : Dimens.scale100);
	const numberOpacity = useSharedValue(animatesIn ? Dimens.opacity0 : Dimens.opacity100);

	useEffect(() => {
		if (!animatesIn) {
			return;
		}

		scale.value = withTiming(Dimens.scale100, POP_IN_TIMING);
		numberOpacity.value = withDelay(NUMBER_FADE_DELAY, withTiming(Dimens.opacity100, NUMBER_FADE_TIMING));
	}, [animatesIn, numberOpacity, scale]);

	const badgeStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }));
	const numberStyle = useAnimatedStyle(() => ({ opacity: numberOpacity.value }));

	return (
		<Animated.View style={[styles.badge, badgeStyle]}>
			<Animated.Text style={[styles.number, numberStyle]}>
				{count > Constants.badgeMaxCount ? `${Constants.badgeMaxCount}+` : count}
			</Animated.Text>
		</Animated.View>
	);
};

const styles = StyleSheet.create({
	badge: {
		backgroundColor: Colors.green,
		position: 'absolute',
		top: Dimens.paddingMinus3,
		right: Dimens.paddingMinus3,
		height: Dimens.size16,
		minWidth: Dimens.size16,
		borderRadius: Dimens.borderRadius99,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: Dimens.padding3,
		paddingVertical: Dimens.padding1,
	},
	number: {
		color: Colors.white,
		fontSize: Dimens.fontSize10,
		fontWeight: Dimens.fontWeight700,
		textAlign: 'center',
	},
});

export default NotificationBadge;
